import type { ReactNode } from 'react'
import React, { createContext, useContext, useEffect, useMemo, useState } from 'react'

import { useWebViewBridge } from '@fourthwall/webview-bridge'

type Props = { children: ReactNode }

export type PreviewMode = 'desktop' | 'mobile'

export type DirtyContextType = {
  isDirty: boolean
  setIsDirty: (isDirty: boolean) => void
}

export const DirtyContext = createContext<DirtyContextType | undefined>(undefined)

export const DirtyProvider = ({ children }: Props) => {
  const [isDirty, setIsDirty] = useState(false)
  const webViewBridge = useWebViewBridge()

  useEffect(() => {
    setSiteDesignerDirty(isDirty)
  }, [isDirty])

  const setSiteDesignerDirty = async (isDirty: boolean) => {
    await webViewBridge.methods.setSiteDesignerDirty?.(isDirty)
  }

  const value = useMemo(
    () => ({
      isDirty,
      setIsDirty,
    }),
    [isDirty, setIsDirty],
  )

  return <DirtyContext.Provider value={value}>{children}</DirtyContext.Provider>
}

export const useDirtyContext = () => {
  const dirtyContext = useContext(DirtyContext)

  if (dirtyContext === undefined) {
    throw new Error('dirtyContext must be used within a DirtyProvider')
  }

  return dirtyContext
}
